import { useCallback, useMemo, useState } from "react";
import { Download, FileCheck2, FileClock, Search, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { useRemote } from "../lib/useRemote";
import { PageHeader } from "../components/PageHeader";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { V3Select } from "../components/V3Select";
import { Drawer } from "../components/Overlay";
import { RemotePageState } from "../components/RemotePageState";

type AuditView = {
  key: string;
  sentence: string;
  actorLabel: string;
  objectLabel: string;
  tenantLabel: string;
  occurredAtText: string;
  risk: string;
  result: string;
  verificationStatus: string;
  evidenceAvailable: boolean;
};
type AuditFilters = {
  keyword: string;
  from: string;
  to: string;
  risk: string;
  result: string;
  verification: string;
};
type AuditVerifyResult = { status?: string; valid?: boolean; severity?: string; checked?: number; brokenSequence?: number; banner?: string };

const riskOptions = [
  { value: "high", label: "高风险" },
  { value: "medium", label: "中风险" },
  { value: "low", label: "低风险" },
];
const resultOptions = [
  { value: "success", label: "已成功" },
  { value: "blocked", label: "已阻断" },
  { value: "failed", label: "已失败" },
];
const verificationOptions = [
  { value: "valid", label: "已校验" },
  { value: "failed", label: "校验失败" },
  { value: "unchecked", label: "待校验" },
];

function recordOf(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function arrayOf(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function stringOf(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function safeText(value: unknown, fallback: string) {
  const text = stringOf(value);
  if (!text) return fallback;
  return text.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, "所选对象").slice(0, 260);
}

function formatTime(value: unknown, fallback = "时间待确认") {
  const text = stringOf(value);
  if (!text) return fallback;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString("zh-CN");
}

function createRequestKey(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function riskLabel(value: string) {
  if (value === "high") return "高风险";
  if (value === "medium") return "中风险";
  if (value === "low") return "低风险";
  return "风险待确认";
}

function resultLabel(value: string) {
  if (value === "success") return "已成功";
  if (value === "blocked") return "已阻断";
  if (value === "failed") return "已失败";
  return "结果待确认";
}

function verificationLabel(value: string) {
  if (value === "valid" || value === "verified") return "已校验";
  if (value === "failed") return "校验失败";
  if (value === "unchecked" || value === "pending") return "待校验";
  return "校验待确认";
}

function badgeForRisk(value: string) {
  if (value === "high") return "failed";
  if (value === "medium") return "pending";
  return "healthy";
}

function badgeForVerification(value: string) {
  if (value === "failed") return "failed";
  if (value === "valid" || value === "verified") return "verified";
  return "pending";
}

function normalizeAuditItem(value: unknown, index: number): AuditView {
  const record = recordOf(value);
  const sentence = safeText(record.sentence ?? record.businessSentence, "业务句子待平台返回。");
  return {
    key: safeText(record.evidenceHandle ?? `${sentence}-${index}`, `audit-${index}`),
    sentence,
    actorLabel: safeText(record.actorLabel ?? record.actorName, "操作者待确认"),
    objectLabel: safeText(record.objectLabel, "对象待确认"),
    tenantLabel: safeText(record.tenantLabel ?? record.tenantName, "全平台"),
    occurredAtText: formatTime(record.occurredAt ?? record.createdAt),
    risk: stringOf(record.risk) ?? "medium",
    result: stringOf(record.result) ?? "success",
    verificationStatus: stringOf(record.verificationStatus) ?? "pending",
    evidenceAvailable: Boolean(record.evidenceToken ?? record.evidenceHandle),
  };
}

function auditQuery(filters: AuditFilters) {
  const params = new URLSearchParams();
  params.set("limit", "50");
  if (filters.keyword.trim()) params.set("keyword", filters.keyword.trim());
  if (filters.from) params.set("from", new Date(filters.from).toISOString());
  if (filters.to) params.set("to", new Date(filters.to).toISOString());
  if (filters.risk !== "all") params.set("risk", filters.risk);
  if (filters.result !== "all") params.set("result", filters.result);
  if (filters.verification !== "all") params.set("verification", filters.verification);
  return `/api/audit?${params.toString()}`;
}

function auditExportFilters(filters: AuditFilters) {
  return {
    ...(filters.keyword.trim() ? { keyword: filters.keyword.trim() } : {}),
    ...(filters.from ? { from: new Date(filters.from).toISOString() } : {}),
    ...(filters.to ? { to: new Date(filters.to).toISOString() } : {}),
    ...(filters.risk !== "all" ? { risk: filters.risk } : {}),
    ...(filters.result !== "all" ? { result: filters.result } : {}),
    ...(filters.verification !== "all" ? { verification: filters.verification } : {}),
  };
}

export function AuditPage() {
  const [filters, setFilters] = useState<AuditFilters>({ keyword: "", from: "", to: "", risk: "high", result: "all", verification: "failed" });
  const [verification, setVerification] = useState<AuditVerifyResult | null>(null);
  const [feedback, setFeedback] = useState("");
  const [drawerItem, setDrawerItem] = useState<AuditView | null>(null);
  const loader = useCallback(async () => {
    const data = await api<{ items: unknown[] }>(auditQuery(filters));
    return { items: arrayOf(data.items).map(normalizeAuditItem) };
  }, [filters]);
  const remote = useRemote(loader, (value) => value.items.length === 0);

  const columns = useMemo<Array<DataTableColumn<AuditView>>>(() => [
    { key: "sentence", header: "业务句子", width: "34%", render: (item) => <strong>{item.sentence}</strong> },
    { key: "object", header: "对象", width: "14%", render: (item) => item.objectLabel },
    { key: "actor", header: "操作者", width: "14%", render: (item) => item.actorLabel },
    { key: "tenant", header: "企业", width: "12%", render: (item) => item.tenantLabel },
    { key: "time", header: "时间范围", width: "14%", render: (item) => <span className="app-number">{item.occurredAtText}</span> },
    { key: "risk", header: "风险", width: "12%", render: (item) => <span className="security-person-cell"><StatusBadge value={badgeForRisk(item.risk)} /><small>{riskLabel(item.risk)}</small></span> },
    { key: "result", header: "结果", width: "10%", render: (item) => resultLabel(item.result) },
    { key: "verification", header: "校验状态", width: "12%", render: (item) => <span className="security-person-cell"><StatusBadge value={badgeForVerification(item.verificationStatus)} /><small>{verificationLabel(item.verificationStatus)}</small></span> },
  ], []);

  const filterBar = <div className="ui-ledger-filters audit-ledger-filters">
    <label className="ui-ledger-filter-primary audit-keyword-filter"><Search size={15} aria-hidden /><input className="ui-inline-field" aria-label="搜索对象、操作者或企业" placeholder="搜索对象、操作者或企业" value={filters.keyword} onChange={(event) => setFilters({ ...filters, keyword: event.target.value })} /></label>
    <V3Select ariaLabel="风险" value={filters.risk} onChange={(value) => setFilters({ ...filters, risk: value })} options={[{ value: "all", label: "全部风险" }, ...riskOptions]} />
    <V3Select ariaLabel="结果" value={filters.result} onChange={(value) => setFilters({ ...filters, result: value })} options={[{ value: "all", label: "全部结果" }, ...resultOptions]} />
    <V3Select ariaLabel="校验状态" value={filters.verification} onChange={(value) => setFilters({ ...filters, verification: value })} options={[{ value: "all", label: "全部校验状态" }, ...verificationOptions]} />
    <div className="audit-date-range" role="group" aria-label="时间范围">
      <input className="ui-inline-field app-number" aria-label="开始时间" type="datetime-local" value={filters.from} onChange={(event) => setFilters({ ...filters, from: event.target.value })} />
      <span aria-hidden>至</span>
      <input className="ui-inline-field app-number" aria-label="结束时间" type="datetime-local" value={filters.to} onChange={(event) => setFilters({ ...filters, to: event.target.value })} />
    </div>
  </div>;

  async function exportEvidence() {
    setFeedback("");
    try {
      await api("/api/audit/export", { method: "POST", body: JSON.stringify({ filters: auditExportFilters(filters), idempotencyKey: createRequestKey("audit-export") }) });
      setFeedback("导出证据申请已提交，文件会使用脱敏业务口径生成。");
    } catch (failure) {
      setFeedback(failure instanceof Error ? failure.message : "导出证据失败，请重试。");
    }
  }

  async function verify() {
    setFeedback("");
    try {
      setVerification(await api<AuditVerifyResult>("/api/audit/verify"));
    } catch (failure) {
      setFeedback(failure instanceof Error ? failure.message : "审计链验证失败，请重试。");
    }
  }

  return <div className="page">
    <PageHeader
      icon={FileClock}
      title="审计链"
      meta={<span>业务句子、正式筛选、高危校验失败</span>}
      actions={<button className="button primary" onClick={() => void verify()}><FileCheck2 size={16} />验证完整链</button>}
    />
    {verification && <div className={verification.valid === false || verification.status === "failed" ? "form-error audit-verification" : "success-message audit-verification"} role="alert">
      {verification.banner ?? (verification.valid === false ? `最高级异常：审计链校验失败，请立即冻结高风险操作。` : `审计链已验证 ${verification.checked ?? 0} 条记录。`)}
    </div>}
    {feedback && <div className="security-risk-note" role="status">{feedback}</div>}
    <SectionCard
      icon={ShieldAlert}
      title="操作证据"
      variant="ledger"
      className="audit-ledger-card"
      filters={filterBar}
      action={<button type="button" className="button secondary" onClick={() => void exportEvidence()}><Download size={16} />导出证据</button>}
    >
      <RemotePageState state={remote.state} onRetry={remote.reload} emptyHint="当前筛选没有审计记录。">
        {(data) => <DataTable
          ariaLabel="审计链操作证据"
          rows={data.items}
          columns={columns}
          rowKey={(item) => item.key}
          rowActions={(item) => <button type="button" className="button ghost" disabled={!item.evidenceAvailable} onClick={() => setDrawerItem(item)}>查看证据</button>}
          empty="当前没有审计记录。"
        />}
      </RemotePageState>
    </SectionCard>
    {drawerItem && <Drawer title="审计证据" description="证据已脱敏展示；完整导出由平台生成文件。" onClose={() => setDrawerItem(null)}>
      <div className="security-evidence-grid">
        <span>业务句子<strong>{drawerItem.sentence}</strong></span>
        <span>操作者<strong>{drawerItem.actorLabel}</strong></span>
        <span>对象<strong>{drawerItem.objectLabel}</strong></span>
        <span>企业<strong>{drawerItem.tenantLabel}</strong></span>
        <span>校验状态<strong>{verificationLabel(drawerItem.verificationStatus)}</strong></span>
      </div>
    </Drawer>}
  </div>;
}
